import { useEffect, useState } from 'react'
import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { Plus, SearchX, Users, Wallet } from 'lucide-react'
import { useForm, Controller, type Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  listAccounts,
  createAccount,
  isLiabilityType,
  type AccountSummary,
  type AccountType,
  type CreateAccountPayload,
} from '@/api/accounts'
import { getEarmarks } from '@/api/goals'
import { getLoanDetail } from '@/api/loans'
import { ROUTES } from '@/routes/routes'
import { useFormatCurrency, useListAmountFormat } from '@/hooks/useFormatCurrency'
import { DESKTOP_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'
import { useIsReadOnly } from '@/hooks/useIsReadOnly'
import { useReadOnlyKnown } from '@/hooks/useReadOnlyKnown'
import { useHouseholdMembers } from '@/hooks/useHouseholdMembers'
import { AccountFiltersBar } from '@/components/accounts/AccountFiltersBar'
import { AccountRow } from '@/components/accounts/AccountRow'
import { AccountsHero } from '@/components/accounts/AccountsHero'
import { ArchiveAccountDialog } from '@/components/accounts/ArchiveAccountDialog'
import { AmountCalculatorInput } from '@/components/AmountCalculatorInput'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { IconPicker } from '@/components/IconPicker'
import { ACCOUNT_ICON_OPTIONS } from '@/lib/accountIconOptions'
import { readyToSpend } from '@/lib/accountDisplay'
import { track } from '@/lib/analytics'
import {
  NO_ACCOUNT_FILTERS,
  matchesAccountFilters,
  narrowsAccountTotals,
  visibleSideTotal,
  type AccountFilters,
} from '@/lib/accountFilters'

const SUBTYPED_KINDS = ['BANK', 'INVESTMENT'] as const

const ACCOUNT_SUB_TYPES: Record<
  (typeof SUBTYPED_KINDS)[number],
  { value: string; label: string }[]
> = {
  BANK: [
    { value: 'SAVINGS', label: 'Savings' },
    { value: 'CURRENT', label: 'Current / Checking' },
    { value: 'SALARY', label: 'Salary' },
  ],
  INVESTMENT: [
    { value: 'RD', label: 'Recurring Deposit (RD)' },
    { value: 'FD', label: 'Fixed Deposit (FD)' },
    { value: 'RETIREMENT', label: 'Retirement Account' },
    { value: 'MARKET_LINKED', label: 'Mutual Funds / Stocks' },
  ],
}

const ACCOUNT_KINDS = [
  { value: 'BANK', label: 'Bank Account' },
  { value: 'CASH', label: 'Cash' },
  { value: 'DIGITAL_WALLET', label: 'Digital Wallet' },
  { value: 'INVESTMENT', label: 'Investment' },
  { value: 'CREDIT_CARD', label: 'Credit Card' },
  { value: 'RECEIVABLE', label: 'Money Owed to You' },
  { value: 'PAYABLE', label: 'Money You Owe' },
  { value: 'PROPERTY', label: 'Property' },
]

const createAccountSchema = z
  .object({
    kind: z.enum([
      'BANK',
      'CASH',
      'DIGITAL_WALLET',
      'INVESTMENT',
      'CREDIT_CARD',
      'RECEIVABLE',
      'PAYABLE',
      'PROPERTY',
    ]),
    name: z.string().min(1, 'Name is required').max(50),
    icon: z.string().optional(),
    subType: z.string().optional(),
    openingBalance: z.coerce.number(),
    creditLimit: z.coerce.number().optional(),
  })
  .refine(
    (v) => !SUBTYPED_KINDS.includes(v.kind as (typeof SUBTYPED_KINDS)[number]) || !!v.subType,
    {
      message: 'Sub-type is required',
      path: ['subType'],
    }
  )
  .refine((v) => v.kind !== 'CREDIT_CARD' || (v.creditLimit ?? 0) > 0, {
    message: 'Credit limit is required',
    path: ['creditLimit'],
  })
  // CREDIT_CARD / PAYABLE (money you owe) and RECEIVABLE (money owed to you) are entered
  // as a positive amount here; the value is stored with the right sign server-side. Mirrors
  // FinancialAccountServiceImpl.validateBalanceSign.
  .refine(
    (v) => !['CREDIT_CARD', 'PAYABLE', 'RECEIVABLE'].includes(v.kind) || v.openingBalance >= 0,
    {
      message: 'Enter the amount as a positive number (use 0 for none)',
      path: ['openingBalance'],
    }
  )

type CreateAccountForm = z.infer<typeof createAccountSchema>

const LABEL_CLS = 'block text-sm font-medium text-[var(--fl-text-1)] mb-1'

export function AddAccountModal({ onClose }: { onClose: () => void }) {
  const { symbol } = useFormatCurrency()
  const queryClient = useQueryClient()
  const members = useHouseholdMembers()
  const [deselected, setDeselected] = useState<Set<string>>(new Set())

  function toggleOwner(id: string) {
    setDeselected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting },
  } = useForm<CreateAccountForm>({
    resolver: zodResolver(createAccountSchema) as Resolver<CreateAccountForm>,
    defaultValues: { kind: 'BANK' },
  })
  const kind = watch('kind')

  // CREDIT_CARD / PAYABLE balances are stored negative (money owed); the field takes a
  // positive "amount owed" and it's negated on submit. RECEIVABLE is money owed to you —
  // positive, no flip.
  const owedInput = kind === 'CREDIT_CARD' || kind === 'PAYABLE'
  const balanceLabel =
    kind === 'PROPERTY'
      ? `Purchase Value (${symbol})`
      : owedInput
        ? `Amount Currently Owed (${symbol})`
        : kind === 'RECEIVABLE'
          ? `Amount Owed to You (${symbol})`
          : `Opening Balance (${symbol})`
  const balanceHint = owedInput
    ? 'Enter what you owe as a positive number. Use 0 if nothing is outstanding.'
    : kind === 'RECEIVABLE'
      ? 'Enter what you are owed as a positive number.'
      : kind === 'PROPERTY'
        ? null
        : kind === 'DIGITAL_WALLET'
          ? 'The balance shown in the wallet app or on the card.'
          : 'Can be negative if the account is overdrawn.'

  useEffect(() => {
    setValue('subType', '')
  }, [kind, setValue])

  const mutation = useMutation({
    mutationFn: (payload: CreateAccountPayload) => createAccount(payload),
    onSuccess: (_data, payload) => {
      track('account_added', { type: payload.type })
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      // This modal can now open from the Dashboard too, so its net worth figure
      // needs to refresh, not just the Accounts list — see AddTransactionModal's
      // matching invalidation for the same reason.
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      // First account ticks the Dashboard onboarding checklist's "add an account" step.
      queryClient.invalidateQueries({ queryKey: ['onboarding'] })
      onClose()
    },
  })

  function onSubmit(values: CreateAccountForm) {
    mutation.mutate({
      type: values.kind,
      name: values.name,
      icon: values.icon || undefined,
      subType: SUBTYPED_KINDS.includes(values.kind as (typeof SUBTYPED_KINDS)[number])
        ? values.subType
        : undefined,
      openingBalance:
        values.kind === 'CREDIT_CARD' || values.kind === 'PAYABLE'
          ? -Math.abs(values.openingBalance)
          : values.openingBalance,
      creditLimit: values.kind === 'CREDIT_CARD' ? values.creditLimit : undefined,
      ownerMemberIds: members.map((m) => m.id).filter((id) => !deselected.has(id)),
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-[var(--fl-surface)] rounded-xl shadow-[0_8px_40px_rgba(0,0,0,0.12)] w-full max-w-md mx-4 p-6">
        <h2
          className="text-[24px] font-semibold text-[var(--fl-heading)] mb-4"
          style={{ fontFamily: '"Source Serif 4", Georgia, serif' }}
        >
          Add Account
        </h2>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className={LABEL_CLS}>Account Type</label>
            <Controller
              control={control}
              name="kind"
              render={({ field }) => (
                <Select
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  aria-label="Account Type"
                >
                  {ACCOUNT_KINDS.map((k) => (
                    <option key={k.value} value={k.value}>
                      {k.label}
                    </option>
                  ))}
                </Select>
              )}
            />
            {kind === 'DIGITAL_WALLET' && (
              <p className="text-xs text-[var(--fl-text-3)] mt-1">
                Paytm, Amazon Pay, PhonePe wallet balance, FASTag, meal cards, gift cards. Not UPI —
                UPI pays straight from your bank.
              </p>
            )}
          </div>
          <div>
            <label className={LABEL_CLS}>Account Name</label>
            <Input {...register('name')} placeholder="e.g. SBI Savings" />
            {errors.name && (
              <p className="text-xs text-[var(--fl-error)] mt-1">{errors.name.message}</p>
            )}
          </div>
          <div>
            <label className={LABEL_CLS}>Icon (optional)</label>
            <Controller
              control={control}
              name="icon"
              render={({ field }) => (
                <IconPicker
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  options={ACCOUNT_ICON_OPTIONS}
                />
              )}
            />
          </div>
          {SUBTYPED_KINDS.includes(kind as (typeof SUBTYPED_KINDS)[number]) && (
            <div>
              <label className={LABEL_CLS}>Sub-Type</label>
              <Controller
                control={control}
                name="subType"
                render={({ field }) => (
                  <Select
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    aria-label="Sub-Type"
                  >
                    <option value="">Select type</option>
                    {ACCOUNT_SUB_TYPES[kind as (typeof SUBTYPED_KINDS)[number]].map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </Select>
                )}
              />
              {errors.subType && (
                <p className="text-xs text-[var(--fl-error)] mt-1">{errors.subType.message}</p>
              )}
            </div>
          )}
          <div>
            <label className={LABEL_CLS}>{balanceLabel}</label>
            <Controller
              control={control}
              name="openingBalance"
              render={({ field }) => (
                <AmountCalculatorInput
                  name={field.name}
                  value={field.value?.toString() ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder="0.00"
                />
              )}
            />
            {balanceHint && <p className="text-xs text-[var(--fl-text-3)] mt-1">{balanceHint}</p>}
            {errors.openingBalance && (
              <p className="text-xs text-[var(--fl-error)] mt-1">{errors.openingBalance.message}</p>
            )}
          </div>
          {kind === 'CREDIT_CARD' && (
            <div>
              <label className={LABEL_CLS}>Credit Limit ({symbol})</label>
              <Input
                {...register('creditLimit')}
                type="number"
                step="0.01"
                placeholder="e.g. 1,00,000"
              />
              {errors.creditLimit && (
                <p className="text-xs text-[var(--fl-error)] mt-1">{errors.creditLimit.message}</p>
              )}
            </div>
          )}
          {members.length > 0 && (
            <div>
              <label className={LABEL_CLS}>Owners</label>
              <div className="space-y-1.5">
                {members.map((m) => (
                  <label
                    key={m.id}
                    className="flex items-center gap-2 text-sm text-[var(--fl-text-1)]"
                  >
                    <Checkbox checked={!deselected.has(m.id)} onChange={() => toggleOwner(m.id)} />
                    {m.name}
                  </label>
                ))}
              </div>
            </div>
          )}
          {mutation.isError && (
            <p className="text-xs text-[var(--fl-error)]">
              {(mutation.error as { response?: { data?: { detail?: string } } })?.response?.data
                ?.detail ?? 'Something went wrong. Please try again.'}
            </p>
          )}
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || mutation.isPending}
              className="flex-1 font-semibold"
            >
              {mutation.isPending ? 'Adding…' : 'Add Account'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

type GroupDef = { key: string; title: string; types: AccountType[] }

const HAVE_GROUPS: GroupDef[] = [
  { key: 'bank', title: 'Bank & savings', types: ['BANK'] },
  { key: 'cash', title: 'Cash & wallets', types: ['CASH', 'DIGITAL_WALLET'] },
  { key: 'investment', title: 'Investments', types: ['INVESTMENT'] },
  { key: 'property', title: 'Property', types: ['PROPERTY'] },
  { key: 'receivable', title: 'Owed to you', types: ['RECEIVABLE'] },
]

const OWE_GROUPS: GroupDef[] = [
  { key: 'loan', title: 'Loans', types: ['LOAN'] },
  { key: 'card', title: 'Credit cards', types: ['CREDIT_CARD'] },
  { key: 'payable', title: 'You owe', types: ['PAYABLE'] },
]

const TYPE_ORDER: AccountType[] = [...HAVE_GROUPS, ...OWE_GROUPS].flatMap((g) => g.types)

function sideValue(a: AccountSummary): number {
  return a.netWorthValue ?? Math.abs(a.balance)
}

// People balances aren't accounts, but they're part of the totals (lending cash or splitting a
// bill keeps the money the household's), so they get a row that links to People for the detail.
function PeopleRow({ label, amount, owe }: { label: string; amount: number; owe: boolean }) {
  const format = useListAmountFormat()
  return (
    <li className="border-b border-[var(--fl-divider)] last:border-b-0">
      <Link
        to={ROUTES.APP.PEOPLE}
        className="flex items-center gap-2.5 px-3 py-2 hover:bg-[var(--fl-surface-hover)] md:gap-3 md:px-4 md:py-3"
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-[var(--fl-surface-subtle)] text-[var(--fl-text-2)] md:h-9 md:w-9 md:rounded-xl">
          <Users aria-hidden="true" className="size-[15px] md:size-[17px]" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-[var(--fl-heading)] md:text-base">
            {label}
          </span>
          <span className="block text-[11.5px] text-[var(--fl-text-3)] md:text-xs">
            Open People
          </span>
        </span>
        <span
          className={`font-serif text-[15px] font-semibold tabular-nums md:text-[17px] ${owe ? 'text-[var(--fl-expense)]' : 'text-[var(--fl-heading)]'}`}
        >
          {format(amount)}
        </span>
      </Link>
    </li>
  )
}

function SectionLabel({ title, count, total }: { title: string; count: number; total?: number }) {
  const format = useListAmountFormat()
  return (
    <h3 className="flex items-baseline justify-between px-1.5 pt-3 pb-1.5 text-[11px] md:pt-4 md:pb-2 font-semibold tracking-[0.08em] text-[var(--fl-text-3)] uppercase">
      <span>
        {title} · {count}
      </span>
      {total !== undefined && <span className="tabular-nums">{format(total)}</span>}
    </h3>
  )
}

function GroupCard({ children }: { children: React.ReactNode }) {
  return (
    <ul className="overflow-hidden rounded-xl bg-[var(--fl-surface)] shadow-[var(--card-shadow-sm)] md:rounded-2xl">
      {children}
    </ul>
  )
}

function HeroSkeleton() {
  return <div className="h-[180px] animate-pulse rounded-[20px] bg-[var(--fl-surface-subtle)]" />
}

export default function AccountsPage() {
  const format = useListAmountFormat()
  const wide = useMediaQuery(DESKTOP_QUERY)
  const isReadOnly = useIsReadOnly()
  const readOnlyKnown = useReadOnlyKnown()
  const members = useHouseholdMembers()
  const [searchParams, setSearchParams] = useSearchParams()

  // ?add=1 (an Analytics nudge) opens the form ready to fill; dropping the param keeps a refresh from reopening it.
  const [showAdd, setShowAdd] = useState(() => searchParams.get('add') === '1' && !isReadOnly)
  // Only a form a link opened waits for read-only to be known: a click on Add comes after the page loads.
  const [openedByLink] = useState(showAdd)
  useEffect(() => {
    if (searchParams.get('add') === null) return
    const next = new URLSearchParams(searchParams)
    next.delete('add')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])
  const [pendingArchiveAccount, setPendingArchiveAccount] = useState<AccountSummary | null>(null)
  const [filters, setFilters] = useState<AccountFilters>(NO_ACCOUNT_FILTERS)

  const { data, isLoading, isError } = useQuery({
    queryKey: filters.showArchived ? ['accounts', 'with-archived'] : ['accounts'],
    queryFn: () => listAccounts(undefined, { includeArchived: filters.showArchived }),
    // Toggling Show archived swaps queries; keep the current list up instead of flashing the loader.
    placeholderData: keepPreviousData,
  })

  const allAccounts = data?.accounts ?? []
  const active = allAccounts.filter((a) => !a.archivedAt)

  const earmarkAccounts = active.filter((a) => a.type === 'BANK' || a.type === 'CASH')
  const earmarkQueries = useQueries({
    queries: earmarkAccounts.map((a) => ({
      queryKey: ['earmarks', a.id],
      queryFn: () => getEarmarks(a.id),
    })),
  })
  const earmarked: Record<string, number | undefined> = {}
  earmarkAccounts.forEach((a, i) => {
    earmarked[a.id] = earmarkQueries[i]?.data?.earmarked
  })

  const loans = active.filter((a) => a.type === 'LOAN')
  const loanQueries = useQueries({
    queries: loans.map((a) => ({
      queryKey: ['loan', a.id],
      queryFn: () => getLoanDetail(a.id),
    })),
  })
  const emiByLoan = new Map(loans.map((a, i) => [a.id, loanQueries[i]?.data?.emiAmount ?? null]))

  const memberName = new Map(members.map((m) => [m.id, m.name]))
  function ownerNames(a: AccountSummary): string | null {
    const names = a.ownerMemberIds.map((id) => memberName.get(id)).filter((n): n is string => !!n)
    // Names only tell accounts apart when some members are left out.
    if (members.length < 2 || names.length === 0 || names.length >= members.length) return null
    const first = names.map((n) => n.split(' ')[0])
    return first.length > 2
      ? `${first.slice(0, 2).join(', ')} +${first.length - 2}`
      : first.join(', ')
  }

  const visible = allAccounts.filter((a) => matchesAccountFilters(a, filters))
  const visibleActive = visible.filter((a) => !a.archivedAt)
  const visibleArchived = visible.filter((a) => a.archivedAt)
  const visibleAssets = visibleActive.filter((a) => !isLiabilityType(a.type))
  const visibleLiabilities = visibleActive.filter((a) => isLiabilityType(a.type))
  // People balances aren't accounts, so a name or type filter hides them.
  const showPeople = !filters.q.trim() && !filters.type
  const owedByPeople = showPeople && filters.side !== 'LIABILITIES' ? (data?.owedByPeople ?? 0) : 0
  const owedToPeople = showPeople && filters.side !== 'ASSETS' ? (data?.owedToPeople ?? 0) : 0
  const hasAssets = visibleAssets.length > 0 || owedByPeople > 0
  const hasLiabilities = visibleLiabilities.length > 0 || owedToPeople > 0
  const householdIsEmpty =
    active.length === 0 && (data?.owedByPeople ?? 0) <= 0 && (data?.owedToPeople ?? 0) <= 0
  const narrowed = narrowsAccountTotals(filters)
  const assetsTotal = data
    ? narrowed
      ? (visibleSideTotal(visibleAssets, owedByPeople) ?? data.totalAssets)
      : data.totalAssets
    : 0
  const liabilitiesTotal = data
    ? narrowed
      ? (visibleSideTotal(visibleLiabilities, owedToPeople) ?? data.totalLiabilities)
      : data.totalLiabilities
    : 0
  const typeOptions = TYPE_ORDER.filter((type) => {
    if (filters.side === 'ASSETS' && isLiabilityType(type)) return false
    if (filters.side === 'LIABILITIES' && !isLiabilityType(type)) return false
    return allAccounts.some((a) => a.type === type && (filters.showArchived || !a.archivedAt))
  })
  // Side-by-side columns only make sense with two sides to show — a lone section spans
  // full width instead of leaving the other grid cell blank.
  const showTwoColumns = hasAssets && hasLiabilities

  const haveCount =
    active.filter((a) => !isLiabilityType(a.type)).length + ((data?.owedByPeople ?? 0) > 0 ? 1 : 0)
  const oweCount =
    active.filter((a) => isLiabilityType(a.type)).length + ((data?.owedToPeople ?? 0) > 0 ? 1 : 0)

  function row(a: AccountSummary) {
    return (
      <AccountRow
        key={a.id}
        account={a}
        ownerNames={ownerNames(a)}
        emiAmount={emiByLoan.get(a.id)}
        onArchive={setPendingArchiveAccount}
      />
    )
  }

  function groups(
    defs: GroupDef[],
    accounts: AccountSummary[],
    people: { label: string; amount: number; owe: boolean } | null
  ) {
    return defs.map((g, i) => {
      const inGroup = accounts.filter((a) => g.types.includes(a.type))
      // The People row closes the side's last group (Owed to you / You owe).
      const withPeople = i === defs.length - 1 && people && people.amount > 0 ? people : null
      if (inGroup.length === 0 && !withPeople) return null
      const total = inGroup.reduce((s, a) => s + sideValue(a), 0) + (withPeople?.amount ?? 0)
      return (
        <div key={g.key}>
          <SectionLabel
            title={g.title}
            count={inGroup.length + (withPeople ? 1 : 0)}
            total={total}
          />
          <GroupCard>
            {inGroup.map(row)}
            {withPeople && <PeopleRow {...withPeople} />}
          </GroupCard>
        </div>
      )
    })
  }

  return (
    <div className="p-4 md:p-10">
      <header className="mb-3 flex items-center justify-between gap-3 md:mb-4">
        <h1 className="font-serif text-xl font-semibold text-[var(--fl-heading)] md:text-[30px]">
          Accounts
        </h1>
        {!isReadOnly && (
          // The floating ＋ adds a transaction, so on a phone this stays a quiet pill rather than a second loud button.
          <Button
            aria-label="Add account"
            variant={wide ? 'default' : 'outline'}
            onClick={() => setShowAdd(true)}
            className={`font-semibold active:scale-95 ${
              wide
                ? 'h-10 rounded-xl px-4 shadow-sm'
                : 'h-9 gap-1 rounded-full border-[var(--fl-primary)] px-3 text-[13px] text-[var(--fl-primary)]'
            } ${householdIsEmpty && !isLoading ? 'motion-safe:animate-attention-pulse' : ''}`}
          >
            <Plus size={wide ? 16 : 14} /> {wide ? 'Add account' : 'Account'}
          </Button>
        )}
      </header>

      <div className="mb-3 md:mb-6">
        {isLoading ? (
          <HeroSkeleton />
        ) : data ? (
          <AccountsHero
            data={data}
            haveCount={haveCount}
            oweCount={oweCount}
            readyToSpend={readyToSpend(active, earmarked)}
          />
        ) : null}
      </div>

      {isError && <p className="text-[var(--fl-error)] text-sm">Failed to load accounts.</p>}

      {data && (
        <>
          {allAccounts.length > 0 && (
            <AccountFiltersBar value={filters} onChange={setFilters} types={typeOptions} />
          )}

          <div className={showTwoColumns ? 'grid grid-cols-1 gap-8 lg:grid-cols-2' : ''}>
            {hasAssets && (
              <section className="mb-6 md:mb-8">
                <div className="flex items-baseline justify-between px-1.5">
                  <h2 className="font-serif text-[15px] font-semibold text-[var(--fl-heading)] md:text-xl">
                    What you have
                  </h2>
                  <span className="font-serif text-[15px] font-semibold tabular-nums text-[var(--fl-income)] md:text-lg">
                    {format(assetsTotal)}
                  </span>
                </div>
                {groups(HAVE_GROUPS, visibleAssets, {
                  label: 'People owe you',
                  amount: owedByPeople,
                  owe: false,
                })}
              </section>
            )}

            {hasLiabilities && (
              <section className="mb-6 md:mb-8">
                <div className="flex items-baseline justify-between px-1.5">
                  <h2 className="font-serif text-[15px] font-semibold text-[var(--fl-heading)] md:text-xl">
                    What you owe
                  </h2>
                  <span className="font-serif text-[15px] font-semibold tabular-nums text-[var(--fl-expense)] md:text-lg">
                    {format(liabilitiesTotal)}
                  </span>
                </div>
                {groups(OWE_GROUPS, visibleLiabilities, {
                  label: 'You owe people',
                  amount: owedToPeople,
                  owe: true,
                })}
              </section>
            )}
          </div>

          {visibleArchived.length > 0 && (
            <section aria-label="Archived accounts" className="mb-8">
              <SectionLabel title="Archived" count={visibleArchived.length} />
              <GroupCard>{visibleArchived.map(row)}</GroupCard>
            </section>
          )}

          {!hasAssets &&
            !hasLiabilities &&
            visibleArchived.length === 0 &&
            (householdIsEmpty && allAccounts.length === 0 ? (
              <EmptyState
                icon={Wallet}
                title="Start with one account"
                description="A bank account, card, cash, or a loan — everything else in LogMySpends builds on this first one."
              />
            ) : (
              <EmptyState
                icon={SearchX}
                title="No accounts match"
                description="Try a different name or type, or clear the filters."
                secondaryAction={{
                  label: 'Clear filters',
                  onClick: () => setFilters(NO_ACCOUNT_FILTERS),
                }}
              />
            ))}
        </>
      )}

      {showAdd && !isReadOnly && (readOnlyKnown || !openedByLink) && (
        <AddAccountModal onClose={() => setShowAdd(false)} />
      )}

      <ArchiveAccountDialog
        account={pendingArchiveAccount}
        onClose={() => setPendingArchiveAccount(null)}
      />
    </div>
  )
}
